import { fetchSsrHtml, fetchSsrStyles, waitForElement } from "../../core/common/ssr-utils.js";
import { getPosExtId } from "./utils.js";
import { getSsrBaseUrl } from "./coursesu-env.js";

/**
 * CoursesU have their own recipe catalog which has nothing to do with ours (and which they released in the same version as they released ours)
 * We currently maintain order-buttons on the recipes of their catalog, which are recipe-card-ctas that are put directly in their recipes.
 *
 * Their catalog only exposes a recipe title (no Mealz recipe id / ext-id), so the CTA is fetched from the SSR API
 * by name: the server resolves the title to a recipe id and renders nothing when there is no exact match.
 *
 * SSR HTML is prefetched in fetchFeaturesHTML (before globalThis.mealz is waited on) so module scripts load early;
 * DOM injection runs later from the cache, once initMealz has completed.
 */

const RECIPE_CARD_CTA_HTML_ENDPOINT = '/v3/recipe-card-cta';
const RECIPE_CARD_CTA_STYLES_ENDPOINT = '/v3/styles/recipe-card/recipe-card-cta';

const seenCoursesURecipes = [];
const htmlByRecipeName = new Map();
const fetchInFlight = new Map();

let lastHeaders = null;
let stylesLoaded = false;
let injectTimeout = null;
let observerStarted = false;

const parsers = [
  // CoursesU recipe catalog
  {
    href: /\/mieux-consommer\//,
    waitSelector: '.recipe-tile',
    findRecipes: () => {
      return Array.from(document.getElementsByClassName('recipe-tile')).map(element => {
        return {
          name: element.querySelector('h2.recipe-name').textContent.trim(),
          parentElement: element.getElementsByClassName('recipe-details')[0]
        };
      });
    }
  },
  // CoursesU recipe details
  {
    href: /\/Recette_[^/]+\.html/,
    waitSelector: '.recipe-main-header',
    findRecipes: () => {
      return [{
        name: document.getElementsByClassName('recipe-main-header')[0].children[0].textContent.trim(),
        parentElement: document.querySelector('div.entry--key-information')
      }];
    }
  }
];

export function isOrderButtonsPage() {
  return parsers.some(p => globalThis.location.href.match(p.href));
}

function getParser() {
  return parsers.find(p => globalThis.location.href.match(p.href));
}

async function waitForRecipeElements() {
  const parser = getParser();
  if (!parser?.waitSelector) {
    return;
  }
  await waitForElement(parser.waitSelector, { maxAttempts: 20, intervalMs: 250 });
}

function findRecipesOnPage() {
  const parser = getParser();
  return parser ? parser.findRecipes().filter(recipe => recipe.name && recipe.parentElement) : [];
}

function ensureStylesLoaded() {
  if (stylesLoaded) {
    return;
  }
  stylesLoaded = true;
  fetchSsrStyles(RECIPE_CARD_CTA_STYLES_ENDPOINT, getSsrBaseUrl());
}

async function fetchButtonHtml(headers, recipeName) {
  if (htmlByRecipeName.has(recipeName)) {
    return htmlByRecipeName.get(recipeName);
  }
  if (fetchInFlight.has(recipeName)) {
    return fetchInFlight.get(recipeName);
  }

  const promise = fetchSsrHtml(RECIPE_CARD_CTA_HTML_ENDPOINT, headers, {
    recipe_name: recipeName,
    store_id: getPosExtId(),
  }, getSsrBaseUrl())
    .then((html) => {
      htmlByRecipeName.set(recipeName, html);
      fetchInFlight.delete(recipeName);
      return html;
    })
    .catch((err) => {
      fetchInFlight.delete(recipeName);
      throw err;
    });

  fetchInFlight.set(recipeName, promise);
  return promise;
}

export async function prefetchOrderButtons(headers) {
  lastHeaders = headers;
  ensureStylesLoaded();
  await waitForRecipeElements();

  const recipes = findRecipesOnPage();
  const recipeNames = [...new Set(recipes.map(recipe => recipe.name))];

  await Promise.all(
    recipeNames.map(async (recipeName) => {
      try {
        await fetchButtonHtml(headers, recipeName);
      } catch (err) {
        console.warn(
          `[CoursesU] order-button SSR prefetch failed for recipe "${recipeName}"`,
          err
        );
      }
    })
  );
}

function ensureObserver() {
  if (observerStarted) {
    return;
  }
  observerStarted = true;

  new MutationObserver(() => {
    clearTimeout(injectTimeout);
    injectTimeout = setTimeout(() => checkForCoursesURecipes(), 100);
  }).observe(document.body, { attributes: false, childList: true, subtree: true });
}

/**
 * Inject prefetched order buttons into the DOM.
 */
export function injectOrderButtons() {
  ensureObserver();
  checkForCoursesURecipes();
}

function checkForCoursesURecipes() {
  const recipes = findRecipesOnPage();
  recipes.forEach(recipe => {
    // Ignore recipes that already got seen
    if (seenCoursesURecipes.includes(recipe.parentElement)) {
      return;
    }
    seenCoursesURecipes.push(recipe.parentElement);

    if (recipe.name) {
      injectButton(recipe, recipe.name);
    }
  });
}

async function injectButton(recipe, recipeName) {
  try {
    let html = htmlByRecipeName.get(recipeName);

    // Recipes discovered after prefetch (e.g. infinite scroll) are fetched on demand
    if (html === undefined && lastHeaders) {
      html = await fetchButtonHtml(lastHeaders, recipeName);
    }

    if (!html?.trim()) {
      return;
    }

    const container = document.createElement("div");
    container.classList.add("mealz-button-parent-card-container");
    container.insertAdjacentHTML("beforeend", html);
    recipe.parentElement.appendChild(container);
  } catch (err) {
    console.warn(
      `[CoursesU] order-button SSR injection failed for recipe "${recipeName}"`,
      err
    );
  }
}
